import React, { useState, useEffect } from "react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import MicIcon from "@mui/icons-material/Mic";
import {
    Box,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Paper,
    CircularProgress,
    Typography,
    Snackbar,
    Alert
} from "@mui/material";

const VoiceSearch = () => {
    const [open, setOpen] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [error, setError] = useState(null);

    const {
        transcript,
        resetTranscript,
        listening,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();

    const handleMicClick = () => {
        resetTranscript();
        setOpen(true);
        setMessage("");
        setSearchResults([]);
        setError(null);
        SpeechRecognition.startListening({ continuous: false });
    };

    const handleClose = () => {
        SpeechRecognition.stopListening();
        setOpen(false);
        resetTranscript();
        setSearchResults([]);
    };

    const handleSearch = async () => {
        if (!transcript.trim()) return;

        const cleanedTranscript = transcript.trim().replace(/[^\w\s]/gi, '');
        console.log("Cleaned transcript: ", cleanedTranscript); // Debugging log

        setLoading(true);
        try {
            const response = await fetch("https://music-endpoint.onrender.com/voice/song", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ songname: cleanedTranscript }),
            });

            const result = await response.json();
            setSearchResults(result.data || []);
            setMessage(result.message || "Search completed.");
            setTimeout(() => {
                setOpen(false);
            }, 10000);
        } catch (err) {
            setError("Failed to search songs. Please try again.");
            setSnackbarOpen(true);
        }
        setLoading(false);
    };


    useEffect(() => {
        if (!listening && transcript) {
            handleSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listening, transcript]);


    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setError(null);
    };

    if (!browserSupportsSpeechRecognition) {
        return <Typography>Browser doesn't support speech recognition.</Typography>;
    }

    return (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
            <IconButton
                color="primary"
                onClick={handleMicClick}
                sx={{ fontSize: 40 }}
                disabled={listening}
            >
                <MicIcon sx={{ fontSize: 40, color: "pink", marginBottom: "20px" }} />
            </IconButton>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{listening ? "Listening..." : "Processing"}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle1" gutterBottom>
                        Transcript:
                    </Typography>
                    <Paper elevation={2} sx={{ padding: 2, minHeight: "60px" }}>
                        {transcript || <em>Say something...</em>}
                    </Paper>

                    {loading && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && searchResults.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6">Search Results:</Typography>
                            <ul>
                                {searchResults.map((song, index) => (
                                    <li key={index}>
                                        {song.song}
                                    </li>
                                ))}
                            </ul>
                        </Box>
                    )}

                    {!loading && !searchResults.length && transcript && (
                        <Typography sx={{ mt: 2 }}>
                            <em>No results found.</em>
                        </Typography>
                    )}

                    {!loading && message && (
                        <Typography sx={{ mt: 2 }} color="success.main">
                            {message}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="error">Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default VoiceSearch;
